import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import { useAuth } from '../../Contexts/AuthContext';
import { submitFeedback } from '../../Helpers/helpApi';

/**
 * FeedbackWidget — thumbs up/down + optional comment, submitted to /help/feedback
 * Props:
 *   contentId: string | number
 *   contentType: 'article' | 'faq' | 'video'
 */
export default function FeedbackWidget({ contentId, contentType }) {
  const auth = useAuth();
  const [rating, setRating] = useState(null);       // true = up, false = down, null = not rated
  const [comment, setComment] = useState('');
  const [commentOpen, setCommentOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [commentError, setCommentError] = useState('');

  const handleRating = (value) => {
    setRating(value);
    setCommentOpen(true);
    setSubmitted(false);
    setError(null);
  };

  const handleCommentChange = (e) => {
    const val = e.target.value;
    if (val.length > 500) {
      setCommentError('Comment must be 500 characters or fewer.');
    } else {
      setCommentError('');
    }
    setComment(val);
  };

  const handleSubmit = async () => {
    if (rating === null) return;
    if (comment.length > 500) return;

    setSubmitting(true);
    setError(null);

    try {
      const response = await submitFeedback(
        { content_id: contentId, content_type: contentType, rating, comment: comment || null },
        auth.activeUser?.accessToken
      );
      if (response?.status === 201 || response?.status === 200) {
        setSubmitted(true);
        setCommentOpen(false);
      } else {
        setError('Could not submit feedback. Please try again.');
      }
    } catch (err) {
      setError('Could not submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
        Was this helpful?
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title="Yes, helpful">
          <IconButton
            size="small"
            onClick={() => handleRating(true)}
            aria-label="Mark as helpful"
            color={rating === true ? 'primary' : 'default'}
          >
            {rating === true ? <ThumbUpIcon fontSize="small" /> : <ThumbUpOutlinedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Not helpful">
          <IconButton
            size="small"
            onClick={() => handleRating(false)}
            aria-label="Mark as not helpful"
            color={rating === false ? 'error' : 'default'}
          >
            {rating === false ? <ThumbDownIcon fontSize="small" /> : <ThumbDownOutlinedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>

        {submitted && (
          <Typography variant="caption" color="success.main" sx={{ ml: 1 }}>
            Thanks for your feedback!
          </Typography>
        )}
      </Box>

      <Collapse in={commentOpen && !submitted}>
        <Box sx={{ mt: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            multiline
            rows={2}
            placeholder="Optional: tell us more (500 chars max)"
            value={comment}
            onChange={handleCommentChange}
            error={!!commentError}
            helperText={commentError || `${comment.length}/500`}
            inputProps={{ maxLength: 501, 'aria-label': 'Feedback comment' }}
          />
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button
              size="small"
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || comment.length > 500}
              startIcon={submitting ? <CircularProgress size={14} /> : null}
            >
              Submit
            </Button>
            <Button size="small" onClick={() => setCommentOpen(false)}>
              Skip
            </Button>
          </Box>
        </Box>
      </Collapse>

      {error && (
        <Alert severity="error" sx={{ mt: 1 }} action={
          <Button size="small" onClick={handleSubmit}>Retry</Button>
        }>
          {error}
        </Alert>
      )}
    </Box>
  );
}
